// Browser-side error capture. Mounted from __root.tsx on the client only.
// Logs window 'error' and 'unhandledrejection' events with route context so
// SSR-hydration crashes and async failures appear in the preview console
// with enough detail to diagnose (stack, route, user-agent).

let installed = false;

import { captureClientException } from "./sentry-browser";

// Detect stale dynamic-chunk load failures (after deploys / Vite re-optimize).
// These leave the app blank because the route module never resolves.
const CHUNK_ERR_RE =
  /(Loading chunk \S+ failed|error loading dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed)/i;

const RELOAD_KEY = "__lov_chunk_reload_at";

function maybeRecoverFromChunkError(err: unknown): boolean {
  const msg =
    err instanceof Error ? `${err.name}: ${err.message}` : typeof err === "string" ? err : "";
  if (!CHUNK_ERR_RE.test(msg)) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
    if (Date.now() - last < 10_000) return false; // avoid reload loop
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage may be unavailable */
  }
  // eslint-disable-next-line no-console
  console.warn("[client:chunk-reload] stale module detected, reloading", msg);
  window.location.reload();
  return true;
}

export function installClientErrorCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const ctx = () => ({
    route: window.location.pathname + window.location.search,
    href: window.location.href,
    userAgent: navigator.userAgent,
    at: new Date().toISOString(),
  });

  window.addEventListener("error", (event) => {
    const err = event.error ?? event.message;
    if (maybeRecoverFromChunkError(err)) return;
    // eslint-disable-next-line no-console
    console.error("[client:error]", {
      ...ctx(),
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      message: event.message,
      stack: err instanceof Error ? err.stack : undefined,
      error: err,
    });
    captureClientException(err, { source: "window.error", ...ctx() });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (maybeRecoverFromChunkError(reason)) return;
    // eslint-disable-next-line no-console
    console.error("[client:unhandledrejection]", {
      ...ctx(),
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
      reason,
    });
    captureClientException(reason, { source: "unhandledrejection", ...ctx() });
  });
}
